import React__default, { useRef, useState, useEffect, useCallback } from 'react';
import cn from 'classnames';
import { objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { childrenToText, useClickOutside, focusedElement } from '../../utils.js';
import { KEY_DOWN, KEY_UP, KEY_ESC } from './use-typeahead.js';
import DropdownContext from './context.js';
import styles from 'ui-components/Dropdown.scss';

var _excluded = ["className", "placeholder", "variant", "disabled"];
var Search = function Search(_ref) {
  var className = _ref.className;
  return /*#__PURE__*/React__default.createElement("svg", {
    className: className,
    width: "20px",
    height: "20px",
    viewBox: "0 0 20 20",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React__default.createElement("g", {
    id: "Page-1",
    stroke: "none",
    strokeWidth: "1",
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/React__default.createElement("g", {
    id: "ikoner",
    transform: "translate(-1156.000000, -266.000000)",
    fill: "#3D3835",
    fillRule: "nonzero"
  }, /*#__PURE__*/React__default.createElement("path", {
    d: "M1164.5,266 C1169.19442,266 1173,269.80558 1173,274.5 C1173,276.486983 1172.31822,278.314728 1171.17583,279.76206 L1175.6066,284.192388 L1174.19239,285.606602 L1169.76206,281.175829 C1168.31473,282.318218 1166.48698,283 1164.5,283 C1159.80558,283 1156,279.19442 1156,274.5 C1156,269.80558 1159.80558,266 1164.5,266 Z M1164.5,268 C1160.91015,268 1158,270.910149 1158,274.5 C1158,278.089851 1160.91015,281 1164.5,281 C1168.08985,281 1171,278.089851 1171,274.5 C1171,270.910149 1168.08985,268 1164.5,268 Z",
    id: "Combined-Shape"
  }))));
};
var SearchableDropdown = function SearchableDropdown(_ref2) {
  var _cn, _cn2, _cn3;
  var className = _ref2.className,
    _ref2$placeholder = _ref2.placeholder,
    placeholder = _ref2$placeholder === void 0 ? "Search for option…" : _ref2$placeholder,
    variant = _ref2.variant,
    disabled = _ref2.disabled,
    props = _objectWithoutPropertiesLoose(_ref2, _excluded);
  var element = useRef(null);
  var inputElement = useRef(null);
  var _useState = useState(false),
    open = _useState[0],
    setOpen = _useState[1];
  var _useState2 = useState(false),
    visible = _useState2[0],
    setVisbile = _useState2[1];
  var _useState3 = useState(""),
    searchString = _useState3[0],
    setSearchString = _useState3[1];
  var _useState4 = useState("Down"),
    direction = _useState4[0],
    setDirection = _useState4[1];
  var items = React__default.Children.toArray(props.children).map(function (x, i) {
    return {
      index: i,
      value: x.props.value,
      label: childrenToText(x.props.children)
    };
  });
  useEffect(function () {
    if (disabled === true) {
      closeDropdown();
    }
  }, [disabled]);
  var closeDropdown = function closeDropdown() {
    setOpen(false);
    setVisbile(false);
    setSearchString("");
  };
  var openDropdown = function openDropdown() {
    if (disabled === true) {
      return;
    }
    setVisbile(false);
    setOpen(true);
  };
  useEffect(function () {
    if (inputElement.current && open && visible) {
      inputElement.current.focus();
    }
  }, [inputElement, open, visible]);

  // Calc height after opened
  useEffect(function () {
    var calcDirection = function calcDirection(element) {
      var main = element.querySelector("main");
      if (!main || !document.documentElement) {
        return;
      }
      var screenHeight = document.documentElement ? document.documentElement.clientHeight : 0;
      var dropdownRect = element.getBoundingClientRect();
      var menuHeight = main.clientHeight;
      var spaceAtTheBottom = screenHeight - dropdownRect.top - dropdownRect.height - menuHeight;
      var spaceAtTheTop = dropdownRect.top - menuHeight;
      var upward = spaceAtTheBottom < 0 && spaceAtTheTop > spaceAtTheBottom;

      // Set state only if there's a relevant difference
      if (upward && direction !== "Up") {
        setDirection("Up");
      }
      if (!upward && direction !== "Down") {
        setDirection("Down");
      }
    };
    if (element.current && open) {
      calcDirection(element.current);
      setVisbile(true);
    }
  }, [open, direction]);
  var focusAtIndex = useCallback(function (index) {
    var item = items.find(function (x) {
      return x.index === index;
    });
    if (index === null && document.activeElement) {
      document.activeElement.blur();
    }
    if (element.current && item && item.value) {
      var itemElement = element.current.querySelector("[data-value=\"" + item.value + "\"]");
      if (itemElement) {
        itemElement.focus();
      }
    }
  }, [element, items]);
  useEffect(function () {
    var current = element.current;
    var focusPrev = function focusPrev(element) {
      if (!element) {
        return;
      }
      var index = parseInt(element.getAttribute("data-index"), 10);
      focusAtIndex(Number.isNaN(index) ? 0 : index - 1);
    };
    var focusNext = function focusNext(element) {
      if (!element) {
        return;
      }
      var index = parseInt(element.getAttribute("data-index"), 10);
      focusAtIndex(Number.isNaN(index) ? 0 : index + 1);
    };
    if (current) {
      var onKeyDown = function onKeyDown(e) {
        switch (e.which) {
          case KEY_ESC:
            return closeDropdown();
          case KEY_UP:
            return focusPrev(focusedElement(current));
          case KEY_DOWN:
            return focusNext(focusedElement(current));
        }
      };
      current.addEventListener("keydown", onKeyDown);
      return function () {
        return current.removeEventListener("keydown", onKeyDown);
      };
    }
  }, [element, focusAtIndex]);
  useClickOutside(element, function () {
    return closeDropdown();
  });
  var onSelect = function onSelect(id) {
    props.onChange(id);
    closeDropdown();
  };
  var selectedChild = Array.isArray(props.children) && props.children.find(function (x) {
    return x.props.value === props.value;
  });
  var selectedInner = selectedChild && selectedChild.props.children;
  var buttonText = selectedInner ? childrenToText(selectedInner) : placeholder;
  var numVisible = items.filter(function (i) {
    return i.label.toLowerCase().includes(searchString.toLowerCase());
  }).length;
  return /*#__PURE__*/React__default.createElement("div", {
    ref: element,
    "data-open": open ? "true" : "false",
    className: cn(styles.block, className, (_cn = {}, _cn[styles.blockOpen] = open && numVisible > 0, _cn), [styles["block" + direction]], (_cn2 = {}, _cn2[styles.hasVariant] = Boolean(variant), _cn2), (_cn3 = {}, _cn3[styles[variant]] = Boolean(variant), _cn3))
  }, !open && /*#__PURE__*/React__default.createElement("button", {
    disabled: disabled === true,
    type: "button",
    "aria-label": buttonText,
    className: styles.head,
    onClick: function onClick(e) {
      e.stopPropagation();
      openDropdown();
    }
  }, /*#__PURE__*/React__default.createElement("div", {
    className: styles.headInner
  }, selectedInner || placeholder), /*#__PURE__*/React__default.createElement("div", {
    className: styles.searchIconArea
  }, /*#__PURE__*/React__default.createElement(Search, {
    className: styles.searchIcon
  }))), open && /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("div", {
    className: styles.head
  }, /*#__PURE__*/React__default.createElement("input", {
    ref: inputElement,
    type: "text",
    placeholder: placeholder,
    className: styles.headInner,
    value: searchString,
    onChange: function onChange(x) {
      return setSearchString(x.target.value);
    }
  }), /*#__PURE__*/React__default.createElement("button", {
    type: "button",
    className: styles.searchIconArea,
    onClick: closeDropdown
  }, /*#__PURE__*/React__default.createElement(Search, {
    className: styles.searchIcon
  }))), /*#__PURE__*/React__default.createElement("main", {
    className: styles.body,
    style: {
      visibility: visible && numVisible > 0 ? "visible" : "hidden"
    }
  }, /*#__PURE__*/React__default.createElement(DropdownContext.Provider, {
    value: {
      onSelect: onSelect,
      value: props.value,
      styles: styles,
      items: items,
      searchString: searchString
    }
  }, props.children))));
};

export default SearchableDropdown;
//# sourceMappingURL=searchable.js.map
